/**
 * Why a Workfront request failed, as the client classifies it.
 *
 * `RATE_LIMIT`, `SERVER` and `NETWORK` are transient and retried inside the call;
 * every other category is returned to the caller on the first attempt. `UNSUPPORTED`
 * is a field, object or action this API version does not know; `SHAPE` is a
 * success whose body is not the shape the client expects.
 */
export enum WorkfrontErrorCategory {
	AUTH = 'AUTH',
	PERMISSION = 'PERMISSION',
	NOT_FOUND = 'NOT_FOUND',
	VALIDATION = 'VALIDATION',
	RATE_LIMIT = 'RATE_LIMIT',
	SERVER = 'SERVER',
	NETWORK = 'NETWORK',
	UNSUPPORTED = 'UNSUPPORTED',
	SHAPE = 'SHAPE',
	UNKNOWN = 'UNKNOWN',
}

/**
 * The kind of Workfront object a configured ID points at (`wf_configured_id.kind`).
 * The adapter's code reads each kind differently, so the set is owned by the code.
 */
export enum WorkfrontConfiguredIdKind {
	FORM = 'form',
	PARAMETER = 'parameter',
	PARAMETER_GROUP = 'parameter_group',
	TEMPLATE = 'template',
	TEMPLATE_TASK = 'template_task',
	TEAM = 'team',
	STATUS = 'status',
}

/** The adapter step a sync error was raised in (`wf_sync_error.step`). */
export enum WorkfrontSyncErrorStep {
	DISCOVER = 'discover',
	FETCH = 'fetch',
	MAP = 'map',
	APPLY = 'apply',
	REFERENCE = 'reference',
	ENVIRONMENT = 'environment',
}

/** A process the adapter runs (`wf_run.process`, `wf_step_run.process`). */
export enum WorkfrontProcess {
	ENVIRONMENT_CHECK = 'environment_check',
	REFERENCE_REFRESH = 'reference_refresh',
	DISCOVERY = 'discovery',
	BUNDLE_FETCH = 'bundle_fetch',
}

/** The steps of a reference refresh, one step run per reference kind. */
export enum WorkfrontReferenceStep {
	REFRESH_KIND = 'refresh_kind',
}

/** What started a run. */
export enum WorkfrontRunTrigger {
	SCHEDULE = 'schedule',
	REQUEST = 'request',
	RETRY = 'retry',
}

/** Where a run stands. `partial` finished with some steps failed or held. */
export enum WorkfrontRunStatus {
	RUNNING = 'running',
	SUCCEEDED = 'succeeded',
	PARTIAL = 'partial',
	FAILED = 'failed',
}

/** What the last environment check found for a configured ID. */
export enum WorkfrontConfiguredIdStatus {
	UNCHECKED = 'unchecked',
	OK = 'ok',
	MISSING = 'missing',
	CHANGED = 'changed',
}

/** `error` stops what it touches; `warning` is reported and the work goes on. */
export enum WorkfrontSyncErrorSeverity {
	ERROR = 'error',
	WARNING = 'warning',
}

/** An error's lifecycle: one open row per problem until it is resolved or ignored. */
export enum WorkfrontSyncErrorStatus {
	OPEN = 'open',
	RETRYING = 'retrying',
	RESOLVED = 'resolved',
	IGNORED = 'ignored',
}

/** The problems the adapter reports; each has a readable title, explanation and fix. */
export enum WorkfrontSyncErrorCode {
	CONFIG_ID_NOT_FOUND = 'CONFIG_ID_NOT_FOUND',
	CONFIG_ID_CHANGED = 'CONFIG_ID_CHANGED',
	/** A role a process needs has no configured ID at all. */
	CONFIG_ID_UNSET = 'CONFIG_ID_UNSET',
	DICTIONARY_DRIFT = 'DICTIONARY_DRIFT',
	WORKFRONT_REQUEST_FAILED = 'WORKFRONT_REQUEST_FAILED',
	/** A bundle fetch failed on something other than a Workfront request, such as storing what it read. */
	BUNDLE_FETCH_FAILED = 'BUNDLE_FETCH_FAILED',
	PARENT_MISSING = 'PARENT_MISSING',
	PARENT_AMBIGUOUS = 'PARENT_AMBIGUOUS',
	GENERATOR_COUNT_MISMATCH = 'GENERATOR_COUNT_MISMATCH',
	REMOVED_IN_WORKFRONT = 'REMOVED_IN_WORKFRONT',
	STREAM_LAG = 'STREAM_LAG',
	BUNDLE_TOO_LARGE = 'BUNDLE_TOO_LARGE',
	// Mapping: field mappings
	MAPPED_FIELD_MISSING = 'MAPPED_FIELD_MISSING',
	MAPPED_FIELD_TYPE_CHANGED = 'MAPPED_FIELD_TYPE_CHANGED',
	FIELD_VALUE_INVALID = 'FIELD_VALUE_INVALID',
	MISSING_REQUIRED = 'MISSING_REQUIRED',
	RULE_FAILED = 'RULE_FAILED',
	/** A legacy field and its connected twin disagree; the connected one wins. */
	LEGACY_CONNECTED_DISAGREE = 'LEGACY_CONNECTED_DISAGREE',
	// Mapping: value mappings
	UNKNOWN_VALUE = 'UNKNOWN_VALUE',
	AMBIGUOUS_VALUE = 'AMBIGUOUS_VALUE',
	PRODUCT_BRAND_MISMATCH = 'PRODUCT_BRAND_MISMATCH',
	// Mapping: the asset-type table
	UNKNOWN_ASSET_TYPE = 'UNKNOWN_ASSET_TYPE',
	ASSET_DETAIL_EMPTY = 'ASSET_DETAIL_EMPTY',
	// Mapping: lookups
	UNKNOWN_LOOKUP_VALUE = 'UNKNOWN_LOOKUP_VALUE',
	/** A `region_to_pm` rule names a user who is no longer an active PM; the next rule up is used. */
	PM_RULE_INACTIVE_PM = 'PM_RULE_INACTIVE_PM',
	// Apply: writing a canonical record to work orders
	APPLY_CONFLICT = 'APPLY_CONFLICT',
	APPLY_RULE_VIOLATION = 'APPLY_RULE_VIOLATION',
	PARENT_WORK_ORDER_MISSING = 'PARENT_WORK_ORDER_MISSING',
	CLASSIFICATION_WRITE_FAILED = 'CLASSIFICATION_WRITE_FAILED',
	/** ATLAS has no locale for the brief's language in its country; the locale is left empty. */
	NO_LOCALE_FOR_COUNTRY = 'NO_LOCALE_FOR_COUNTRY',
}

/**
 * The scope keys and flags the adapter reads (`wf_sync_scope.key`). The keys are
 * the code's; their values are data, each validated by its own schema.
 */
export enum WorkfrontScopeKey {
	ADAPTER_ENABLED = 'adapter.enabled',
	REFERENCE_SCHEDULE_ENABLED = 'reference.schedule_enabled',
	BRIEF_TYPES = 'brief_types',
	CLOSED_STATUSES_PROJ = 'closed_statuses.PROJ',
	CLOSED_STATUSES_TASK = 'closed_statuses.TASK',
	FIELD_LISTS_PROJ = 'field_lists.PROJ',
	FIELD_LISTS_OPTASK = 'field_lists.OPTASK',
	FIELD_LISTS_TASK = 'field_lists.TASK',
	FIELD_LISTS_DOCU = 'field_lists.DOCU',
	FIELD_LISTS_NOTE = 'field_lists.NOTE',
	CADENCE_DISCOVERY_MINUTES = 'cadences.discovery_minutes',
	CADENCE_FULL_REFETCH_HOUR_UTC = 'cadences.full_refetch_hour_utc',
	FINGERPRINT_EXCLUSIONS = 'fingerprint_exclusions',
	REOPEN_WINDOW_DAYS = 'transitions.reopen_window_days',
	DISCOVERY_BUDGET_SECONDS = 'discovery.budget_seconds',
	DISCOVERY_CHUNK_SIZE = 'discovery.chunk_size',
	BUNDLES_ON_DEMAND_ENABLED = 'bundles.on_demand_enabled',
	DISCOVERY_HOURLY_ENABLED = 'discovery.hourly_enabled',
	DISCOVERY_NIGHTLY_ENABLED = 'discovery.nightly_enabled',
	BUNDLE_REQUESTS_WARNING = 'limits.bundle_requests_warning',
	BUNDLE_SECONDS_WARNING = 'limits.bundle_seconds_warning',
	/** Map each new bundle version into its canonical record. Off: bundles return to `clean`. */
	MAPPING_ENABLED = 'mapping.enabled',
	/** How many bundles a publish re-maps per minute. */
	REMAP_BATCH_PER_MINUTE = 'remap.batch_per_minute',
	/** The parameter groups the description is composed from, per level. */
	DESCRIPTION_GROUPS = 'description_groups',
	/** What the work-order sync does with a mapped version: nothing, record it, or apply it. */
	APPLY_MODE = 'apply.mode',
	/** How many work orders one apply run writes before continuing. */
	APPLY_SLICE_SIZE = 'apply.slice_size',
	/** While set, the nightly run fetches only open bundles updated since this date. */
	APPLY_BACKFILL_SINCE = 'apply.backfill_since',
	/** Whether the work order page offers Refresh from Workfront. */
	WORK_ORDER_PAGE_REFRESH_ENABLED = 'work_order_page.refresh_enabled',
}

/**
 * The roles of the configured IDs that define the sync scope: the agency team,
 * the forms that identify a brief, and the templates briefs are created from.
 * Other roles, such as one per mapped field, are free snake_case names.
 */
export enum WorkfrontScopeRole {
	AGENCY_TEAM = 'agency_team',
	PARENT_FORM = 'parent_form',
	CHILD_FORM = 'child_form',
	GENERATOR_FORM = 'generator_form',
	PARENT_TEMPLATE = 'parent_template',
	CHILD_TEMPLATE = 'child_template',
	/** The field holding the brief type; a child's must be `Content Factory`. */
	BRIEF_TYPE = 'brief_type',
	/** The calculated field holding the agency team's ID, filtered on by the server. */
	AGENCY_FIELD = 'agency_field',
	/** The generator issue's count of the asset briefs it created. */
	GENERATOR_CREATED_COUNT = 'generator_created_count',
}

/** How well a resolve candidate matches a missing configured ID's description. */
export enum WorkfrontResolveMatch {
	/** The only object with the same type and name, and every described attribute matches. */
	EXACT = 'exact',
	/** Same type and name, but another attribute differs or more than one object qualifies. */
	LIKELY = 'likely',
}

/** The parts of Workfront's catalogue the adapter keeps a copy of (`wf_ref_*`). */
export enum WorkfrontReferenceKind {
	FORM = 'form',
	PARAMETER_GROUP = 'parameter_group',
	PARAMETER = 'parameter',
	TEMPLATE = 'template',
	TEMPLATE_TASK = 'template_task',
	USER = 'user',
	TEAM = 'team',
	ROLE = 'role',
	STATUS = 'status',
	PLANNING_RECORD = 'planning_record',
}

/**
 * Where a bundle is in its life (`wf_bundle.state`), read from its projects at
 * each fetch.
 */
export enum WorkfrontBundleState {
	/** Asset briefs were found, but no Content Factory Brief yet. */
	AWAITING_PARENT = 'awaiting_parent',
	ACTIVE = 'active',
	CLOSED = 'closed',
	OUT_OF_SCOPE = 'out_of_scope',
	REMOVED = 'removed',
	/** More than one Content Factory Brief in one program. */
	AMBIGUOUS = 'ambiguous',
}

/**
 * Where a bundle is in the sync chain (`wf_bundle.sync_status`). A fetch moves
 * it through `dirty`, `fetching` and `failed`; mapping through `mapping`, then
 * `mapped`, or `held` when its parent cannot be mapped; applying adds the rest.
 */
export enum WorkfrontBundleSyncStatus {
	CLEAN = 'clean',
	DIRTY = 'dirty',
	FETCHING = 'fetching',
	MAPPING = 'mapping',
	MAPPED = 'mapped',
	APPLYING = 'applying',
	HELD = 'held',
	FAILED = 'failed',
}

/** What an object is within its bundle (`wf_bundle_object.role`). */
export enum WorkfrontBundleObjectRole {
	PARENT = 'parent',
	CHILD = 'child',
	GENERATOR = 'generator',
	ISSUE = 'issue',
	TASK = 'task',
	NOTE = 'note',
	DOCUMENT = 'document',
}

/** How an object differs between two versions of its bundle. */
export enum WorkfrontBundleChangeKind {
	ADDED = 'added',
	CHANGED = 'changed',
	REMOVED = 'removed',
}

/** Why a bundle was fetched (`wf_bundle_version.cause`). */
export enum WorkfrontFetchReason {
	DISCOVERY = 'discovery',
	NIGHTLY = 'nightly',
	REQUEST = 'request',
	RECONCILE = 'reconcile',
	RETRY = 'retry',
}

/** The discovery streams (`wf_sync_stream.stream_code`). */
export enum WorkfrontStreamCode {
	PARENTS = 'parents',
	CHILDREN = 'children',
	GENERATORS = 'generators',
	ISSUES = 'issues',
	TASKS = 'tasks',
	DOCUMENTS = 'documents',
	NOTES = 'notes',
	DELETIONS = 'deletions',
	TRANSITIONS = 'transitions',
}

/** The steps the adapter records as step runs for bundles and streams. */
export enum WorkfrontBundleStep {
	FETCH = 'fetch',
	DISCOVER = 'discover',
	/** Mapping a stored bundle version into its canonical record. */
	MAP = 'map',
	/** Writing a canonical record to work orders, reported back by the work-order sync. */
	APPLY = 'apply',
}
